import { useQuery } from '@tanstack/react-query';
import { JSX, ReactNode } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';

import { useProductCenterApi } from 'api';
import { userTabsSettingsQuery } from 'api/queries/UserSettingsQueries';
import { DraggableProps } from 'library/components/controls/dnd/Draggable';
import { DraggableCollection } from 'library/components/controls/dnd/DraggableCollection';
import {
	DraggableWrapperProps,
	withDnD,
} from 'library/components/controls/dnd/withDnD';

import { TabConfiguration, TabsProps, TabsSettingsTypes } from './types';

interface DraggableTabsProps extends TabsProps {
	tabsSettingType: TabsSettingsTypes;
	renderTabs: (
		tabs: (TabConfiguration & DraggableProps)[],
		component: (
			props: Omit<
				NavLinkProps &
					React.RefAttributes<HTMLAnchorElement> &
					DraggableWrapperProps,
				'ref'
			> &
				React.RefAttributes<unknown>
		) => ReactNode
	) => JSX.Element;
}

const DraggableNavLink = withDnD(NavLink);

const emptySettings = {
	Unitoverview: [],
	Drivelineview: [],
	Productindividualview: [],
};

export const DraggableTabs = ({
	tabs,
	renderTabs,
	tabsSettingType,
}: DraggableTabsProps) => {
	const { api } = useProductCenterApi();
	const { data } = useQuery({
		...userTabsSettingsQuery(api, 'Tabs'),
		throwOnError: false,
		gcTime: 0,
	});

	const handleOnInitial = async () => {
		const resp = await api.getUserTabsSettings({ category: 'Tabs' });
		const settings = resp?.data?.settings ?? emptySettings;

		return (settings[tabsSettingType] ||
			emptySettings[
				tabsSettingType
			]) as TabConfiguration[keyof TabConfiguration][];
	};

	const handleDragEnd = async (
		newItems: TabConfiguration[keyof TabConfiguration][]
	) => {
		const settings = {
			...(data?.data?.settings || emptySettings),
			[tabsSettingType]: newItems,
		};

		await api.setUserTabsSettings({
			category: 'Tabs',
			settings,
		});
	};

	const renderer = (items: (TabConfiguration & DraggableProps)[]) =>
		renderTabs(items, DraggableNavLink);

	return (
		<DraggableCollection
			data={tabs}
			dataKey="path"
			renderer={renderer}
			onDragEnd={handleDragEnd}
			onInitial={handleOnInitial}
		/>
	);
};
